import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const pipeline = [
    {
        name: "Groundnut De-Oiled Cake",
        text: "Protein-rich groundnut meal for compound cattle and poultry feed formulations.",
    },
    {
        name: "Sesame Oil Cake",
        text: "Expeller-pressed sesame cake, valued in specialty feed and soil nutrition applications.",
    },
    {
        name: "Soybean De-Oiled Cake / Soy Meal",
        text: "Widely traded high-protein soy meal for feed mills and livestock integrators.",
    },
];

const chips = ["Protein 36–38% min", "Moisture 10–12% max", "50 kg export bags", "22–24 MT per 20ft"];

export default function Products() {
    return (
        <>
            <Seo
                title="Products — Rapeseed/Mustard DOC & Oilseed Meals | AXORA PS GLOBAL LLP"
                description="Flagship rapeseed/mustard de-oiled cake (DOC) for animal feed, plus groundnut DOC, sesame oil cake, and soybean meal available on inquiry. Export from Rajasthan, India."
                path="/products"
                ogImage="/images/og-products.jpg"
            />

            <section data-testid="products-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>Products</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            Our Products
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            One flagship product, done properly — plus a focused pipeline of Indian oilseed meals for
                            buyers building broader feed raw-material programs.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="flagship-product" className="border-t border-slate-200 bg-white py-20 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(10,37,64,0.08)] lg:grid-cols-2">
                            <div className="relative min-h-[320px]">
                                <img
                                    src="/images/doc-own-flakes.jpg"
                                    alt="Close-up of mustard de-oiled cake flakes — the actual DOC product exported by AXORA PS GLOBAL LLP"
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                                <span className="absolute left-5 top-5 rounded-md bg-amber px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-navy-deep">
                                    Flagship Product
                                </span>
                            </div>
                            <div className="flex flex-col justify-center p-8 lg:p-14">
                                <h2 className="font-display text-3xl font-bold tracking-tight text-navy">
                                    Rapeseed/Mustard De-Oiled Cake (DOC)
                                </h2>
                                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                    Also traded as Rapeseed-Mustard Meal · Mustard DOC · Rapeseed DOC · Mustard/Rapeseed Oil Cake
                                </p>
                                <p className="mt-5 text-base leading-relaxed text-slate-600">
                                    High-protein animal feed raw material sourced from verified partner processors in
                                    Rajasthan, India — shipped in 50 kg export bags at 22–24 MT per 20ft container
                                    through Mundra, Kandla/Deendayal, JNPT/Nhava Sheva, Pipavav, and Hazira.
                                </p>
                                <div className="mt-7 flex flex-wrap gap-2.5">
                                    {chips.map((chip) => (
                                        <span key={chip} className="rounded-full border border-steel/30 bg-steel-light px-3.5 py-1.5 text-xs font-semibold text-navy">
                                            {chip}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-9 flex flex-wrap gap-4">
                                    <Link
                                        to="/products/rapeseed-mustard-doc"
                                        data-testid="flagship-specs-button"
                                        className="inline-flex items-center gap-2 rounded-md bg-navy px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-navy-hover"
                                    >
                                        View Full Specifications
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                    <WhatsAppButton label="Request Quote on WhatsApp" location="products-flagship" />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section data-testid="pipeline-products" className="bg-mist py-24 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Pipeline</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Pipeline Products</h2>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                            The following oilseed meals are handled on an inquiry basis through the same verified
                            partner network. Share your specification and target quantity for availability and pricing.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
                        {pipeline.map((product, i) => (
                            <Reveal key={product.name} delay={i * 0.1}>
                                <div
                                    data-testid={`pipeline-card-${product.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]"
                                >
                                    <span className="inline-flex w-fit items-center rounded-md border border-amber/50 bg-amber/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-dark">
                                        Available on Inquiry
                                    </span>
                                    <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-navy">{product.name}</h3>
                                    <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{product.text}</p>
                                    <div className="mt-7">
                                        <WhatsAppButton
                                            label="Enquire on WhatsApp"
                                            location={`pipeline-${product.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                            variant="outline"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand location="products" />
        </>
    );
}
